import { useEffect, useRef } from "react";
import { Instagram, ExternalLink, Plus, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Instagram Embed Component - Inline version
 * Shows the actual Instagram post content directly
 */
const InstagramEmbed = ({ postId, url, className = "", onAddToExperience, onCreateExperience }) => {
  const containerRef = useRef(null);
  const embedUrl = url || `https://www.instagram.com/p/${postId}/`;

  useEffect(() => {
    // Load Instagram embed script if not already loaded
    const loadInstagramEmbed = () => {
      if (window.instgrm) {
        window.instgrm.Embeds.process(containerRef.current);
      }
    };

    if (!window.instgrm) {
      const script = document.createElement("script");
      script.src = "https://www.instagram.com/embed.js";
      script.async = true;
      script.onload = loadInstagramEmbed;
      document.body.appendChild(script);
    } else {
      // Small delay to ensure DOM is ready
      setTimeout(loadInstagramEmbed, 100);
    }
  }, [postId, url]);

  return (
    <div ref={containerRef} className={`instagram-embed-wrapper relative group ${className}`}>
      <blockquote
        className="instagram-media"
        data-instgrm-permalink={embedUrl}
        data-instgrm-version="14"
        style={{
          background: "#FFF",
          border: 0,
          borderRadius: "12px",
          boxShadow: "0 0 1px 0 rgba(0,0,0,0.5), 0 1px 10px 0 rgba(0,0,0,0.15)",
          margin: 0,
          maxWidth: "100%",
          minWidth: "280px",
          padding: 0,
          width: "100%"
        }}
      >
        {/* Loading state */}
        <div className="p-6 text-center bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 rounded-xl">
          <Instagram className="w-10 h-10 text-pink-500 mx-auto mb-3 animate-pulse" />
          <p className="text-sm text-gray-600 mb-2">Loading Instagram post...</p>
          <a
            href={embedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-pink-500 hover:underline inline-flex items-center gap-1"
          >
            View on Instagram <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </blockquote>
      
      {/* Action buttons overlay - visible on hover */}
      {(onAddToExperience || onCreateExperience) && (
        <div className="absolute bottom-2 left-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
          {onCreateExperience && (
            <Button
              size="sm"
              onClick={onCreateExperience}
              className="flex-1 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white shadow-lg"
            >
              <Wand2 className="w-3 h-3 mr-1" />
              Create Experience
            </Button>
          )}
          {onAddToExperience && (
            <Button
              size="sm"
              variant="secondary"
              onClick={onAddToExperience}
              className="bg-white/90 hover:bg-white shadow-lg"
            >
              <Plus className="w-3 h-3 mr-1" />
              Add to Exp
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Compact Instagram embed for grid view
 * Shows actual embed content scaled down
 */
export const InstagramEmbedCard = ({ postId, url, onCreateExperience, onAddToExperience, onDelete }) => {
  const containerRef = useRef(null);
  const embedUrl = url || `https://www.instagram.com/p/${postId}/`;

  useEffect(() => {
    const loadInstagramEmbed = () => {
      if (window.instgrm) {
        window.instgrm.Embeds.process(containerRef.current);
      }
    };

    if (!window.instgrm) {
      const script = document.createElement("script");
      script.src = "https://www.instagram.com/embed.js";
      script.async = true;
      script.onload = loadInstagramEmbed;
      document.body.appendChild(script);
    } else {
      setTimeout(loadInstagramEmbed, 100);
    }
  }, [postId, url]);

  return (
    <div className="rounded-2xl overflow-hidden border-0 shadow-sm bg-white group relative">
      {/* Embed container with fixed aspect ratio */}
      <div 
        ref={containerRef} 
        className="w-full overflow-hidden"
        style={{ minHeight: "280px" }}
      >
        <blockquote
          className="instagram-media"
          data-instgrm-permalink={embedUrl}
          data-instgrm-version="14"
          style={{
            background: "#FFF",
            border: 0,
            borderRadius: 0,
            boxShadow: "none",
            margin: 0,
            padding: 0,
            width: "100%"
          }}
        >
          {/* Loading placeholder */}
          <div className="aspect-square bg-gradient-to-br from-purple-100 via-pink-100 to-orange-100 flex flex-col items-center justify-center p-4">
            <Instagram className="w-12 h-12 text-pink-500 mb-3 animate-pulse" />
            <p className="text-sm text-gray-600 text-center">Loading...</p>
            <a
              href={embedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-pink-500 hover:underline mt-2 inline-flex items-center gap-1"
            >
              Open in Instagram <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </blockquote>
      </div>
      
      {/* Action buttons - always visible at bottom */}
      <div className="p-2 bg-gray-50 border-t flex gap-2">
        <Button
          size="sm"
          onClick={onCreateExperience}
          className="flex-1 h-8 text-xs bg-black hover:bg-gray-800"
        >
          <Wand2 className="w-3 h-3 mr-1" />
          Create Experience
        </Button>
        {onDelete && (
          <Button
            size="sm"
            variant="ghost"
            onClick={onDelete}
            className="h-8 text-xs text-red-500 hover:text-red-600 hover:bg-red-50 px-2"
          >
            Delete
          </Button>
        )}
      </div>
    </div>
  );
};

export default InstagramEmbed;
